import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, ImageIcon, Search, X, Download, Trash2 } from "lucide-react";
import api, { API } from "@/lib/api";

const STATUS = ["new", "paid", "in_progress", "completed", "shipped"];
const PAY_FILTERS = ["all", "paid", "pending", "awaiting_confirmation"];

const badge = (ps) => {
  const map = { paid: "bg-green-200", pending: "bg-yellow-200", awaiting_confirmation: "bg-orange-200" };
  return map[ps] || "bg-slate-200";
};

export default function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  const [payFilter, setPayFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const confirm = async (id) => {
    try { await api.put(`/admin/orders/${id}/confirm`); toast.success("Payment confirmed & receipt sent"); load(); }
    catch { toast.error("Failed"); }
  };
  const setStatus = async (id, status) => {
    try { await api.put(`/admin/orders/${id}/status`, { status }); load(); } catch { toast.error("Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Permanently delete this order? This cannot be undone.")) return;
    try { await api.delete(`/admin/orders/${id}`); toast.success("Order deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return orders.filter((o) => {
      if (payFilter !== "all" && o.payment_status !== payFilter) return false;
      if (statusFilter !== "all" && o.status !== statusFilter) return false;
      if (!q) return true;
      const hay = [
        o.customer_name, o.email, o.phone, o.id, o.occasion, o.occasion_details,
        o.referred_by, o.shipping_address,
        ...(o.items || []).map((i) => i.name),
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [orders, query, payFilter, statusFilter]);

  const clearFilters = () => { setQuery(""); setPayFilter("all"); setStatusFilter("all"); };
  const hasFilters = query || payFilter !== "all" || statusFilter !== "all";

  const exportCsv = async () => {
    try {
      const r = await api.get("/admin/orders/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `orders-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Orders exported");
    } catch { toast.error("Export failed"); }
  };

  return (
    <div data-testid="admin-orders">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display font-bold text-3xl mb-1">Orders</h1>
          <p className="text-ink/60">
            {hasFilters ? `Showing ${filtered.length} of ${orders.length} orders.` : `${orders.length} total orders.`}
          </p>
        </div>
        <button onClick={exportCsv} disabled={orders.length === 0} className="brut-btn !py-2 !px-4 bg-sun text-sm disabled:opacity-40" data-testid="orders-export-csv">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      <div className="brut-card p-4 mb-6 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, email, phone, order #, occasion…"
            className="brut-input !py-2 !pl-9 w-full text-sm"
            data-testid="orders-search-input"
          />
        </div>
        <select value={payFilter} onChange={(e) => setPayFilter(e.target.value)} className="brut-input !py-2 !w-auto text-sm" data-testid="orders-payment-filter">
          {PAY_FILTERS.map((p) => <option key={p} value={p}>{p === "all" ? "All payments" : p}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="brut-input !py-2 !w-auto text-sm" data-testid="orders-status-filter">
          <option value="all">All statuses</option>
          {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {hasFilters && (
          <button onClick={clearFilters} className="brut-btn !py-2 !px-4 bg-white text-sm" data-testid="orders-clear-filters">
            <X className="w-4 h-4" /> Clear
          </button>
        )}
      </div>

      <div className="space-y-4">
        {orders.length === 0 && <div className="brut-card p-6 text-ink/60">No orders yet.</div>}
        {orders.length > 0 && filtered.length === 0 && (
          <div className="brut-card p-6 text-ink/60" data-testid="orders-no-match">No orders match your search.</div>
        )}
        {filtered.map((o) => (
          <div key={o.id} className="brut-card p-5" data-testid={`order-row-${o.id.slice(0,8)}`}>
            <div className="flex flex-wrap justify-between gap-3">
              <div>
                <div className="font-display font-bold text-lg">{o.customer_name} <span className="text-ink/40 text-sm">#{o.id.slice(0, 8)}</span></div>
                <div className="text-sm text-ink/60">{o.email} · {o.phone || "no phone"}</div>
                <div className="text-sm mt-1">{o.items.map((i) => `${i.name} ×${i.quantity}`).join(", ")}</div>
                {o.occasion && <span className="tag-pill bg-sky mt-2">For: {o.occasion}{o.occasion_details ? ` — ${o.occasion_details}` : ""}</span>}
                <div className="mt-2">
                  <span className="tag-pill bg-grape/40">Delivery: {({email:"Email", mail:"Mailed", both:"Email + Mailed"}[o.delivery_method] || "Email")}</span>
                  {o.shipping_address && <span className="block text-sm text-ink/60 mt-1 whitespace-pre-line">📦 {o.shipping_address}</span>}
                </div>
                {o.referred_by && <span className={`tag-pill mt-2 ml-2 ${o.referral_verified ? "bg-green-200" : "bg-slate-200"}`}>Ref: {o.referred_by} {o.referral_verified ? "✓" : "?"}</span>}
                {o.notes && <p className="text-sm text-ink/60 mt-2 italic">“{o.notes}”</p>}
              </div>
              <div className="text-right">
                <div className="font-display font-bold text-2xl">${o.total.toFixed(2)}</div>
                {o.discount > 0 && <div className="text-xs text-tangerine">−${o.discount.toFixed(2)} ({o.discount_reason})</div>}
                <span className={`tag-pill mt-2 ${badge(o.payment_status)}`}>{o.payment_status}</span>
                <div className="text-xs text-ink/50 mt-1">{o.payment_method || "—"}</div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 mt-4 pt-4 border-t-2 border-dashed border-ink/20">
              {o.photo_ids?.length > 0 && (
                <button onClick={() => setSelected(selected === o.id ? null : o.id)} className="brut-btn !py-2 !px-4 bg-white text-sm" data-testid={`view-photos-${o.id.slice(0,8)}`}>
                  <ImageIcon className="w-4 h-4" /> {o.photo_ids.length} photo{o.photo_ids.length > 1 ? "s" : ""}
                </button>
              )}
              {o.payment_status !== "paid" && (
                <button onClick={() => confirm(o.id)} className="brut-btn !py-2 !px-4 bg-green-200 text-sm" data-testid={`confirm-payment-${o.id.slice(0,8)}`}>
                  <Check className="w-4 h-4" /> Mark paid & send receipt
                </button>
              )}
              <select value={o.status} onChange={(e) => setStatus(o.id, e.target.value)} className="brut-input !py-2 !w-auto text-sm" data-testid={`status-${o.id.slice(0,8)}`}>
                {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <button onClick={() => remove(o.id)} className="brut-btn !py-2 !px-4 bg-white text-tangerine text-sm ml-auto" data-testid={`delete-order-${o.id.slice(0,8)}`}>
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            </div>

            {selected === o.id && (
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mt-4">
                {o.photo_ids.map((pid) => (
                  <a key={pid} href={`${API}/files/${pid}`} target="_blank" rel="noreferrer">
                    <img src={`${API}/files/${pid}`} alt="reference" className="w-full h-24 object-cover brut border-ink" />
                  </a>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
